class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    // Elements
    this.countdownEl = this.querySelector('[data-pm-cartnotice-countdown]');
    this.totalEl = this.querySelector('[data-pm-cartnotice-total]');
    this.itemsContainer = document.getElementById('cart-notification-product');

    // Countdown storage key
    this._countdownInterval = null;
    this._reserveKey = 'pm_cart_reserve_expires_at';

    // Quantity change click handling (event delegation)
    this.addEventListener('click', (e) => this.onNotificationClick(e));

    // Upsell toggle handling
    this.addEventListener('change', (e) => this.onUpsellChange(e));

    // NEW: make header cart icon open this popup
    this.bindHeaderCartIconOpener();
  }

  /* =========================================================
     REQUIRED API FOR Craft/Dawn product-form.js COMPATIBILITY
  ========================================================== */

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: this.cartItemKey
          ? `[id="cart-notification-product-${this.cartItemKey}"]`
          : '#cart-notification-product',
      },
      {
        id: 'cart-notification-upsell',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  async renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    // Reset reserve timer on add-to-cart
    this.resetReserveCountdown();

    // Render any sections provided by the add-to-cart response
    const sections = this.getSectionsToRender();
    sections.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;

      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHTML) return;

      container.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });

    // Clear any transient junk and render full cart (all items + total)
    if (this.itemsContainer) this.itemsContainer.innerHTML = '';
    this.renderFullCartFromAjax();
    await this.renderUpsellFromSections();

    if (this.header) this.header.reveal();
    this.open();
  }

  async renderUpsellFromSections() {
    try {
      const res = await fetch(`${this.getRoot()}?sections=cart-notification-upsell`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;

      const data = await res.json();
      const html = data && data['cart-notification-upsell'];
      const container = document.getElementById('cart-notification-upsell');
      if (container && typeof html === 'string') container.innerHTML = html;
    } catch (e) {}
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector) || doc.querySelector('.shopify-section');
    return node ? node.innerHTML : '';
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ========================================================= */

  // NEW: header cart icon click -> open popup
  bindHeaderCartIconOpener() {
    // Avoid multiple bindings if theme hot-reloads sections
    if (window.__pmCartIconBound) return;
    window.__pmCartIconBound = true;

    document.addEventListener(
      'click',
      (evt) => {
        // allow new-tab / modifiers to work normally
        if (evt.defaultPrevented) return;
        if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
        if (typeof evt.button === 'number' && evt.button !== 0) return; // only left click

        const target = evt.target;

        // Primary: click on the cart bubble or its contents
        const bubble = target.closest('#cart-icon-bubble');
        if (!bubble) return;

        // Find the clickable thing (a/button) that would normally go to cart
        const trigger = bubble.closest('a, button');
        if (!trigger) return;

        // If it's an anchor to /cart, stop navigation
        if (trigger.tagName === 'A') {
          evt.preventDefault();
        } else {
          // If it's a button (drawer trigger etc.), stop that behaviour too
          evt.preventDefault();
          evt.stopPropagation();
        }

        // Open the popup with fresh cart data
        this.openFromHeaderIcon();
      },
      true
    );
  }

  async openFromHeaderIcon() {
    // Do NOT reset timer here; just resume. If no timer exists, one will be created.
    if (this.itemsContainer) this.itemsContainer.innerHTML = '';
    await this.renderFullCartFromAjax();
    await this.renderUpsellFromSections();

    this.open();
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    // Start countdown display whenever it opens
    this.startOrResumeCountdown();

    // Remove any stray ">" text node that might appear
    setTimeout(() => this.removeStrayChevronTextNodes(), 0);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  /* -----------------------------
     FULL CART RENDERING (ALL ITEMS)
  ------------------------------ */
  async renderFullCartFromAjax() {
    if (!this.itemsContainer) return;

    try {
      const res = await fetch(`${this.getRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) throw new Error(`cart.js fetch failed: ${res.status}`);
      const cart = await res.json();

      // Hide countdown banner if cart is empty
      this.toggleCountdownBanner(cart.item_count > 0);

      // Render items
      this.itemsContainer.innerHTML = this.buildItemsHTML(cart);

      // Kill stray chevron text nodes (">") anywhere in the notification
      this.removeStrayChevronTextNodes();

      // Render total (wrap in .money so any currency converter hooks it)
      if (this.totalEl) this.totalEl.innerHTML = `<span class="money">${this.formatMoney(cart.total_price)}</span>`;

      // Update cart bubble count (best-effort)
      this.updateCartBubble(cart);

      // Let Judge.me enhance badges
      this.refreshJudgeMe();
    } catch (e) {
      // console.warn(e);
    }
  }

  buildItemsHTML(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return `<div class="pm-cartnotice-empty">Your cart is empty.</div>`;
    }

    // Hide these upsell products from the visible list (they still remain in cart + totals)
    const UPSOLD_HANDLES = ['enhanced-reading', 'grand-ritual'];

    const isUpsellItem = (item) => {
      const handle = (item.handle || '').toLowerCase();
      const url = (item.url || '').toLowerCase();

      if (handle && UPSOLD_HANDLES.includes(handle)) return true;
      if (url) return UPSOLD_HANDLES.some((h) => url.includes(`/products/${h}`));
      return false;
    };

    const visibleItems = cart.items.filter((item) => !isUpsellItem(item));

    // If only upsells are in cart, don't show "empty" (because totals would still be non-zero).
    if (visibleItems.length === 0) {
      return '';
    }

    return `
      <div class="pm-cartnotice-list">
        ${visibleItems.map((item) => this.buildItemRowHTML(item)).join('')}
      </div>
    `;
  }

  buildItemRowHTML(item) {
    const img = item.image
      ? `<img src="${this.escapeAttr(item.image)}" alt="${this.escapeAttr(item.product_title || item.title || '')}" loading="lazy">`
      : '';

    const title = this.escapeHTML(item.product_title || item.title || 'Item');

    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="pm-cartnotice-item__variant">${this.escapeHTML(item.variant_title)}</div>`
        : '';

    // YMQ / line item properties (customer-entered fields)
    const props = item.properties && typeof item.properties === 'object' ? item.properties : null;

    const MAX_PROP_CHARS = 100;

    const propertiesHTML = props
      ? Object.entries(props)
          .filter(([k, v]) => {
            if (!k) return false;
            if (String(k).startsWith('_')) return false; // internal
            if (v === null || v === undefined) return false;
            const s = String(v).trim();
            if (!s) return false;
            return true;
          })
          .map(([k, v]) => {
            const key = this.escapeHTML(String(k));

            // Truncate value at 350 characters (display only)
            let rawVal = String(v);
            rawVal = rawVal.replace(/\s+/g, ' ').trim(); // tidy whitespace a bit

            const truncated = rawVal.length > MAX_PROP_CHARS ? rawVal.slice(0, MAX_PROP_CHARS) + '...' : rawVal;
            const val = this.escapeHTML(truncated);

            return `<div class="pm-cartnotice-prop"><span class="pm-cartnotice-prop__label">${key}:</span> <span class="pm-cartnotice-prop__value">${val}</span></div>`;
          })
          .join('')
      : '';

    // Judge.me preview badge placeholder
    const judgeme = item.product_id
      ? `
        <div
          class="jdgm-widget jdgm-preview-badge pm-cartnotice-judgeme"
          data-id="${this.escapeAttr(item.product_id)}"
          data-auto-install="false"
        ></div>
      `
      : '';

    const qty = Number(item.quantity || 1);
    const lineKey = item.key ? this.escapeAttr(item.key) : '';

    const priceCents = qty > 1 ? item.final_line_price : item.final_price;

    return `
      <div class="pm-cartnotice-item" data-line-key="${lineKey}">
        <div class="pm-cartnotice-item__image">${img}</div>

        <div class="pm-cartnotice-item__info">
          <div class="pm-cartnotice-item__title">${title}</div>
          ${variant}
          ${propertiesHTML}
          ${judgeme}

          <div class="pm-cartnotice-qty" aria-label="Quantity controls">
            <button type="button" class="pm-qty-btn" data-qty-action="minus" aria-label="Decrease quantity">−</button>
            <span class="pm-qty-value" data-qty-value>${this.escapeHTML(String(qty))}</span>
            <button type="button" class="pm-qty-btn" data-qty-action="plus" aria-label="Increase quantity">+</button>
          </div>
        </div>

        <div class="pm-cartnotice-item__price">
          <span class="money">${this.formatMoney(priceCents)}</span>
        </div>
      </div>
    `;
  }

  /* -----------------------------
     QUANTITY UPDATE HANDLING
  ------------------------------ */
  onNotificationClick(e) {
    const btn = e.target.closest('[data-qty-action]');
    if (!btn) return;

    const row = btn.closest('[data-line-key]');
    if (!row) return;

    const key = row.getAttribute('data-line-key');
    if (!key) return;

    const action = btn.getAttribute('data-qty-action');
    const valueEl = row.querySelector('[data-qty-value]');
    const currentQty = valueEl ? Number(valueEl.textContent || '1') : 1;

    let nextQty = currentQty;
    if (action === 'plus') nextQty = currentQty + 1;
    if (action === 'minus') nextQty = currentQty - 1;
    if (nextQty < 0) nextQty = 0;

    if (valueEl) valueEl.textContent = String(nextQty);

    row.classList.add('pm-qty-loading');
    this.changeLineItemQuantity(key, nextQty).finally(() => row.classList.remove('pm-qty-loading'));
  }

  async onUpsellChange(e) {
    const cb = e.target.closest('.pm-upsell-checkbox');
    if (!cb) return;

    const variantId = cb.getAttribute('data-upsell-variant-id');
    if (!variantId) return;

    cb.disabled = true;

    try {
      if (cb.checked) {
        // Add upsell
        const res = await fetch(`${this.getRoot()}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
      } else {
        // Remove upsell (prefer line key, most reliable)
        const lineKey = cb.getAttribute('data-upsell-line-key');

        const payload = lineKey ? { id: lineKey, quantity: 0 } : { id: Number(variantId), quantity: 0 };

        const res = await fetch(`${this.getRoot()}cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error('remove failed');
      }

      await this.renderFullCartFromAjax();
      await this.renderUpsellFromSections();

      // Cart changed -> reset reserve timer
      this.resetReserveCountdown();
      this.startOrResumeCountdown();
    } catch (err) {
      // revert checkbox if request failed
      cb.checked = !cb.checked;
    } finally {
      cb.disabled = false;
    }
  }

  async changeLineItemQuantity(lineKey, quantity) {
    try {
      const res = await fetch(`${this.getRoot()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: lineKey, quantity }),
      });

      if (!res.ok) throw new Error(`cart/change.js failed: ${res.status}`);

      await this.renderFullCartFromAjax();
      await this.renderUpsellFromSections();

      // Cart changed -> reset reserve timer
      this.resetReserveCountdown();
      this.startOrResumeCountdown();
    } catch (e) {
      await this.renderFullCartFromAjax();
    }
  }

  updateCartBubble(cart) {
    try {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;

      const countEl =
        bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]') ||
        bubble.querySelector('.cart-count-bubble span');
      if (countEl && cart && typeof cart.item_count === 'number') {
        countEl.textContent = String(cart.item_count);
      }
    } catch (e) {}
  }

  refreshJudgeMe() {
    try {
      if (window.jdgm && typeof window.jdgm.renderBadges === 'function') window.jdgm.renderBadges();
      if (window.JudgeMe && typeof window.JudgeMe.refreshWidgets === 'function') window.JudgeMe.refreshWidgets();
    } catch (e) {}
  }

  /* -----------------------------
     REMOVE STRAY ">" TEXT NODE
  ------------------------------ */
  removeStrayChevronTextNodes() {
    try {
      if (!this.notification) return;

      const walker = document.createTreeWalker(this.notification, NodeFilter.SHOW_TEXT);
      const toRemove = [];

      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node && node.nodeValue && node.nodeValue.trim() === '>') {
          toRemove.push(node);
        }
      }

      toRemove.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    } catch (e) {}
  }

  /* -----------------------------
    COUNTDOWN VISIBILITY (hide when cart empty)
  ------------------------------ */
  toggleCountdownBanner(hasItems) {
    const banner = this.notification.querySelector('.pm-cartnotice-timer');
    if (!banner) return;

    banner.style.display = hasItems ? '' : 'none';
  }

  /* -----------------------------
     COUNTDOWN (10 minutes)
  ------------------------------ */
  resetReserveCountdown() {
    const expiresAt = Date.now() + 10 * 60 * 1000;
    try {
      localStorage.setItem(this._reserveKey, String(expiresAt));
    } catch (e) {}
  }

  startOrResumeCountdown() {
    if (!this.countdownEl) return;

    if (this._countdownInterval) clearInterval(this._countdownInterval);

    const getExpires = () => {
      try {
        const raw = localStorage.getItem(this._reserveKey);
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) ? n : null;
      } catch (e) {
        return null;
      }
    };

    let expiresAt = getExpires();
    if (!expiresAt) {
      this.resetReserveCountdown();
      expiresAt = getExpires();
    }

    const tick = () => {
      const exp = getExpires();
      const remainingMs = exp ? exp - Date.now() : 0;
      const clamped = Math.max(0, remainingMs);

      const totalSeconds = Math.floor(clamped / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (this.countdownEl) {
        this.countdownEl.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }

      if (clamped === 0) {
        this.notification.classList.add('pm-cartnotice-expired');
      } else {
        this.notification.classList.remove('pm-cartnotice-expired');
      }
    };

    tick();
    this._countdownInterval = setInterval(tick, 250);
  }

  /* -----------------------------
     HELPERS
  ------------------------------ */
  getRoot() {
    try {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    } catch (e) {}
    return '/';
  }

  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;

    // Prefer Shopify formatMoney (matches theme)
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        const fmt =
          Shopify.money_format || (window.theme && (window.theme.moneyFormat || window.theme.money_format)) || null;

        if (fmt) return Shopify.formatMoney(cents, fmt);
      }
    } catch (e) {}

    // Strong fallback using active currency code (keeps symbol correct)
    try {
      const code =
        (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        (window.Shopify && Shopify.currency && Shopify.currency.iso_code) ||
        null;

      if (code) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: code,
          currencyDisplay: 'symbol',
          maximumFractionDigits: 2,
        }).format(amount);
      }
    } catch (e) {}

    return amount.toFixed(2);
  }

  escapeHTML(str) {
    return String(str || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  escapeAttr(str) {
    return this.escapeHTML(str).replaceAll('`', '&#096;');
  }
}

customElements.define('cart-notification', CartNotification);
